import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {catchError, from, map, Observable, of, switchMap} from 'rxjs';
import {environment} from '../../environments/environment';
import {ApiTokenService} from './api-token.service';
import {VtkJob, VtkJobDetail, VtkJobMetrics, VtkLogEntry, VtkPaginatedResponse} from './vtk.types';

/** A VTK response as a page renders it: the value, or why there is none. */
export type VtkResult<T> = {value: T; error?: undefined} | {value?: undefined; error: string};

/**
 * Why a VTK request failed, in words a user can act on. VTK's own errors carry {"error": ...};
 * API Gateway's authorizer rejections carry {"message": "Unauthorized"}.
 */
export function vtkErrorMessage(e: unknown): string {
  if (!(e instanceof HttpErrorResponse)) {
    return e instanceof Error ? e.message : String(e);
  }
  if (e.status === 0) {
    // The browser hides the cause of a network/CORS failure from the page; only the console has it.
    return 'VTK could not be reached (network or CORS error, see the browser console)';
  }
  const detail = e.error?.error ?? e.error?.message ?? e.statusText;
  const hint = e.status === 401 ? ' (your login was not accepted by VTK; reloading the page may help)'
    : e.status === 403 ? ' (your role in this organization does not allow this)'
    : '';
  return `${e.status} ${detail}${hint}`;
}

/** Wrap a VTK request so that a failure becomes a VtkResult instead of an error. */
export function vtkResult<T>(request: Observable<T>): Observable<VtkResult<T>> {
  return request.pipe(
    map(value => ({value})),
    catchError(e => of({error: vtkErrorMessage(e)})),
  );
}

@Injectable({providedIn: 'root'})
export class VtkService {

  constructor(private http: HttpClient, private apiToken: ApiTokenService) {}

  /** `viewAs`: the organization the user acts as (the selected one); the path names the job's owner. */
  private get<T>(viewAs: string, path: string): Observable<T> {
    return from(this.apiToken.accessToken()).pipe(switchMap(token => this.http.get<T>(this.url(path), {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'x-castlabs-organization': viewAs,
      })
    })));
  }

  private url(path: string): string {
    return `${environment.VTK_API}${path}`;
  }

  getBundleJobs(orgUrn: string, bundleName: string): Observable<VtkPaginatedResponse<VtkJob>> {
    return this.get<VtkPaginatedResponse<VtkJob>>(orgUrn, `/o/${orgUrn}/jb/${encodeURIComponent(bundleName)}/jobs`);
  }

  getJob(ownerOrg: string, viewAs: string, jobBundle: string, jobId: string): Observable<VtkJobDetail> {
    return this.get<VtkJobDetail>(viewAs, `/o/${ownerOrg}/jb/${encodeURIComponent(jobBundle)}/jobs/${jobId}`);
  }

  getJobMetrics(ownerOrg: string, viewAs: string, jobBundle: string, jobId: string): Observable<VtkJobMetrics> {
    return this.get<VtkJobMetrics>(viewAs, `/o/${ownerOrg}/jb/${encodeURIComponent(jobBundle)}/jobs/${jobId}/metrics`);
  }

  getTaskLog(ownerOrg: string, viewAs: string, jobBundle: string, jobId: string, taskId: number, offset = 0, limit = 1000): Observable<VtkPaginatedResponse<VtkLogEntry>> {
    return this.get<VtkPaginatedResponse<VtkLogEntry>>(viewAs, `/o/${ownerOrg}/jb/${encodeURIComponent(jobBundle)}/jobs/${jobId}/tasks/${taskId}/log?limit=${limit}&offset=${offset}`);
  }
}
